import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import CustomButton from "components/common/Button/CustomButton";
import GetItemFromLocalstorage from "Utils/GetItemFromLocalstorage";
import {
  PaDataTable,
  PaEmptyState,
  PaFilterBar,
  PaFilterChip,
  PaPageHeader,
  PaPagination,
  PaStatusBadge,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import PaAccessDenied from "pages/PlatformAdmin/layout/PaAccessDenied";
import PaRelativeTime from "pages/PlatformAdmin/PaRelativeTime";
import { usePlatformAdmin } from "context/PlatformAdminContext";
import { platformRoleAccess } from "helpers/RolesPermission";
import { getPaginationObj } from "Utils/utils";
import {
  activatePlatformStaffUser,
  deactivatePlatformStaffUser,
  fetchPlatformRoles,
  fetchPlatformStaffUsers,
} from "store/services/platformAdmin.services";
import { URL_PLATFORM_ADMIN_STAFF_USERS } from "store/services/URL";
import StaffUserDrawer from "./StaffUserDrawer";
import {
  actorIsPlatformOwner,
  buildStaffUsersQuery,
  canDeactivateStaff,
  canEditStaffUser,
  collectRolesFromStaff,
  DEFAULT_STAFF_FILTERS,
  hasActiveStaffFilters,
  mergeRoleOptions,
} from "./staffUsers.utils";

const StaffUsersList = () => {
  const currentUser = GetItemFromLocalstorage("user");
  const { refreshPlatformSession } = usePlatformAdmin();
  const canList = platformRoleAccess("platform.staff.list");
  const canCreate = platformRoleAccess("platform.staff.create");
  const canUpdate = platformRoleAccess("platform.staff.update");
  const canManageRoles = platformRoleAccess("platform.roles.manage");

  const [filters, setFilters] = useState(DEFAULT_STAFF_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(getPaginationObj());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [actionStaffId, setActionStaffId] = useState(null);

  const loadRoleOptions = useCallback(
    async (staffRows = []) => {
      const fallbackRoles = collectRolesFromStaff(staffRows);

      if (!canManageRoles) {
        setRoleOptions(fallbackRoles);
        return;
      }

      try {
        const payload = await fetchPlatformRoles({ page: 1, perPage: 100 });
        const roles = payload?.roles?.data ?? [];
        setRoleOptions(mergeRoleOptions(roles, fallbackRoles));
      } catch {
        setRoleOptions(fallbackRoles);
      }
    },
    [canManageRoles]
  );

  const loadStaff = useCallback(async () => {
    if (!canList) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = buildStaffUsersQuery(filters, URL_PLATFORM_ADMIN_STAFF_USERS);
      const payload = await fetchPlatformStaffUsers(endpoint);
      const staffUsers = payload?.staff_users;
      const nextRows = Array.isArray(staffUsers?.data) ? staffUsers.data : [];

      setRows(nextRows);
      setPagination(getPaginationObj(staffUsers, filters));
      await loadRoleOptions(nextRows);
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load platform staff");
    } finally {
      setLoading(false);
    }
  }, [canList, filters, loadRoleOptions]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput.trim()) {
          return prev;
        }

        return { ...prev, search: searchInput.trim(), page: 1 };
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const handleClearFilters = () => {
    setSearchInput("");
    setFilters(DEFAULT_STAFF_FILTERS);
  };

  const openCreateDrawer = () => {
    setSelectedStaff(null);
    setDrawerMode("create");
  };

  const openEditDrawer = (staffUser) => {
    setSelectedStaff(staffUser);
    setDrawerMode("edit");
  };

  const closeDrawer = () => {
    setDrawerMode(null);
    setSelectedStaff(null);
  };

  const handleStaffSaved = async (savedUser) => {
    await loadStaff();
    const isCurrentUser = savedUser?.id === currentUser?.id;
    await refreshPlatformSession({
      silent: !isCurrentUser,
      force: true,
    });
  };

  const handleToggleStatus = async (staffUser) => {
    if (!canUpdate) {
      return;
    }

    if (staffUser.status) {
      const guard = canDeactivateStaff(staffUser, currentUser?.id);
      if (!guard.allowed) {
        toast.error(guard.reason);
        return;
      }
    }

    setActionStaffId(staffUser.id);

    try {
      if (staffUser.status) {
        await deactivatePlatformStaffUser(staffUser.id);
        toast.success("Staff user deactivated");
      } else {
        await activatePlatformStaffUser(staffUser.id);
        toast.success("Staff user activated");
      }

      await loadStaff();
    } catch (err) {
      toast.error(err?.message || "Staff status update failed");
    } finally {
      setActionStaffId(null);
    }
  };

  const headerActions = (
    <div className="flex flex-wrap items-center gap-2">
      {canManageRoles ? (
        <Link
          to="/platform-roles"
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Manage platform roles →
        </Link>
      ) : null}
      {canCreate ? (
        <CustomButton
          label="Add staff"
          clickHandler={openCreateDrawer}
          cssClass="bg-indigo-600 hover:bg-indigo-700 text-white"
        />
      ) : null}
    </div>
  );

  const columns = useMemo(
    () => [
      {
        key: "name",
        label: "Name",
        render: (row) => (
          <div>
            <div className="font-medium text-gray-900">{row.name}</div>
            {row.is_platform_owner ? (
              <div className="text-xs text-indigo-600">Full access</div>
            ) : null}
          </div>
        ),
      },
      {
        key: "email",
        label: "Email",
      },
      {
        key: "platform_roles",
        label: "Platform roles",
        render: (row) => (
          <div className="flex flex-wrap gap-1.5">
            {(row.platform_roles || []).length === 0 ? (
              <span className="text-sm text-gray-500">—</span>
            ) : (
              row.platform_roles.map((role) => (
                <PaStatusBadge
                  key={`${row.id}-${role.id}`}
                  label={role.name}
                  variant={role.is_platform_owner ? "info" : "neutral"}
                />
              ))
            )}
          </div>
        ),
      },
      {
        key: "status",
        label: "Status",
        render: (row) => (
          <PaStatusBadge
            label={row.status ? "Active" : "Disabled"}
            variant={row.status ? "success" : "neutral"}
          />
        ),
      },
      {
        key: "last_login_at",
        label: "Last login",
        render: (row) => <PaRelativeTime value={row.last_login_at} />,
      },
      {
        key: "created_at",
        label: "Created",
        render: (row) => <PaRelativeTime value={row.created_at} />,
      },
      {
        key: "actions",
        label: "Actions",
        headerClassName: "text-right",
        cellClassName: "text-right",
        render: (row) => {
          if (!canUpdate) {
            return "—";
          }

          const editGuard = canEditStaffUser(row, currentUser);
          const deactivateGuard = canDeactivateStaff(row, currentUser?.id, currentUser);
          const disableEdit = !editGuard.allowed;
          const disableDeactivate = row.status && !deactivateGuard.allowed;

          return (
            <div className="inline-flex items-center gap-2">
              <button
                type="button"
                disabled={disableEdit}
                title={disableEdit ? editGuard.reason : undefined}
                onClick={() => openEditDrawer(row)}
                className="rounded-lg px-2 py-1 text-sm font-medium text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:text-gray-400"
              >
                Edit
              </button>
              <button
                type="button"
                disabled={actionStaffId === row.id || disableDeactivate}
                title={disableDeactivate ? deactivateGuard.reason : undefined}
                onClick={() => handleToggleStatus(row)}
                className="rounded-lg px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
              >
                {row.status ? "Deactivate" : "Activate"}
              </button>
            </div>
          );
        },
      },
    ],
    [canUpdate, currentUser, actionStaffId]
  );

  if (!canList) {
    return (
      <PaAccessDenied
        title="Platform staff"
        subtitle="Manage platform team accounts"
        description="You need platform.staff.list to view platform staff."
      />
    );
  }

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title="Platform staff"
        subtitle="Manage platform team accounts and roles"
        actions={headerActions}
      />

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <PaFilterBar
        className="mb-4"
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search name or email"
        filters={
          <>
            <PaFilterChip
              active={filters.status === true}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  status: prev.status === true ? null : true,
                  page: 1,
                }))
              }
            >
              Active
            </PaFilterChip>
            <PaFilterChip
              active={filters.status === false}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  status: prev.status === false ? null : false,
                  page: 1,
                }))
              }
            >
              Disabled
            </PaFilterChip>
          </>
        }
        onClear={hasActiveStaffFilters({ ...filters, search: searchInput }) ? handleClearFilters : undefined}
        resultCount={pagination.total}
      />

      <PaDataTable
        columns={columns}
        rows={rows}
        rowKey="id"
        isLoading={loading}
        emptyState={
          <PaEmptyState title="No platform staff found" message="Try adjusting your filters" />
        }
      />

      <PaPagination
        page={filters.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        rows={rows}
        perPage={filters.perPage}
        loading={loading}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
      />

      <StaffUserDrawer
        open={Boolean(drawerMode)}
        onClose={closeDrawer}
        mode={drawerMode || "create"}
        staffUser={selectedStaff}
        roleOptions={roleOptions}
        currentUser={currentUser}
        onSaved={handleStaffSaved}
      />
    </PlatformAdminLayout>
  );
};

export default StaffUsersList;
